import {
	createLandingMetaPixel,
	deleteLandingMetaPixel,
	fetchLandingMetaPixels,
	updateLandingMetaPixel,
} from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingMetaPixel, MetaPixelConversionEvent } from '@/entities/landing.entity';
import { META_PIXEL_CONVERSION_EVENT_LABELS, validateMetaPixelInput } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import LandingsPageCard from '@/pages/landings/components/landings-page-card';
import LandingsGate from '@/pages/landings/landings-gate';
import { PencilIcon, PlusIcon, TargetIcon, Trash2Icon } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const CONVERSION_EVENTS: MetaPixelConversionEvent[] = ['lead', 'subscribe', 'purchase'];

type PixelFormState = {
	name: string;
	pixelId: string;
	conversionEvent: MetaPixelConversionEvent;
};

const EMPTY_FORM: PixelFormState = {
	name: '',
	pixelId: '',
	conversionEvent: 'lead',
};

function formatPixelsCount(count: number): string {
	if (count === 0) return 'Нет пикселей';
	if (count === 1) return '1 пиксель';
	if (count >= 2 && count <= 4) return `${count} пикселя`;
	return `${count} пикселей`;
}

export default function LandingsMetaPixelsPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<MetaPixelsContent />
			</LandingsGate>
		</PageScroll>
	);
}

function MetaPixelsContent() {
	const [pixels, setPixels] = useState<ILandingMetaPixel[]>([]);
	const [loading, setLoading] = useState(true);
	const [formOpen, setFormOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<ILandingMetaPixel | null>(null);
	const [editing, setEditing] = useState<ILandingMetaPixel | null>(null);
	const [form, setForm] = useState<PixelFormState>(EMPTY_FORM);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const loadPixels = useCallback(async () => {
		setLoading(true);
		try {
			const items = await fetchLandingMetaPixels();
			setPixels(items);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadPixels();
	}, [loadPixels]);

	function openCreate() {
		setEditing(null);
		setForm(EMPTY_FORM);
		setFormOpen(true);
	}

	function openEdit(pixel: ILandingMetaPixel) {
		setEditing(pixel);
		setForm({
			name: pixel.name,
			pixelId: pixel.pixelId,
			conversionEvent: pixel.conversionEvent,
		});
		setFormOpen(true);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const name = form.name.trim();
		const pixelId = form.pixelId.trim();
		const validation = validateMetaPixelInput({ name, pixelId });
		if (!validation.ok) {
			toast.error(validation.message);
			return;
		}

		setSaving(true);
		try {
			if (editing) {
				await updateLandingMetaPixel(editing.id, {
					name,
					pixelId,
					conversionEvent: form.conversionEvent,
				});
				toast.success('Пиксель обновлён');
			} else {
				await createLandingMetaPixel({
					name,
					pixelId,
					conversionEvent: form.conversionEvent,
				});
				toast.success('Пиксель создан');
			}
			setFormOpen(false);
			await loadPixels();
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleting(true);
		try {
			await deleteLandingMetaPixel(deleteTarget.id);
			toast.success('Пиксель удалён');
			setDeleteTarget(null);
			await loadPixels();
		} finally {
			setDeleting(false);
		}
	}

	return (
		<>
			<LandingsPageCard
				icon={TargetIcon}
				title='Meta Pixel'
				description={formatPixelsCount(pixels.length)}
				color='chart-4'
				action={
					<Button type='button' size='sm' onClick={openCreate}>
						<PlusIcon size={16} className='mr-1' />
						Добавить
					</Button>
				}
			>
				{loading ? (
					<div className='flex justify-center py-8'>
						<Spinner />
					</div>
				) : pixels.length === 0 ? (
					<p className='text-sm text-muted-foreground py-4 text-center'>Пикселей пока нет.</p>
				) : (
					<div className='flex flex-col gap-2'>
						{pixels.map(pixel => (
							<div
								key={pixel.id}
								className='rounded-xl border border-border bg-card p-3 shadow-sm flex items-start justify-between gap-3'
							>
								<div className='min-w-0'>
									<p className='font-medium truncate'>{pixel.name}</p>
									<p className='text-xs text-muted-foreground mt-0.5 font-mono'>{pixel.pixelId}</p>
									<p className='text-xs text-muted-foreground mt-1'>
										Конверсия: {META_PIXEL_CONVERSION_EVENT_LABELS[pixel.conversionEvent]}
									</p>
								</div>
								<div className='flex shrink-0 gap-1'>
									<Button type='button' size='icon' variant='ghost' onClick={() => openEdit(pixel)}>
										<PencilIcon size={16} />
									</Button>
									<Button
										type='button'
										size='icon'
										variant='ghost'
										className='text-destructive'
										onClick={() => setDeleteTarget(pixel)}
									>
										<Trash2Icon size={16} />
									</Button>
								</div>
							</div>
						))}
					</div>
				)}
			</LandingsPageCard>

			<Dialog open={formOpen} onOpenChange={setFormOpen}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>{editing ? 'Редактировать пиксель' : 'Новый Meta Pixel'}</DialogTitle>
						<DialogDescription>
							Укажите название для выбора в ссылках, Pixel ID из Facebook и тип конверсии при скачивании.
						</DialogDescription>
					</DialogHeader>
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='meta-pixel-name'>Название</Label>
							<InputGroup>
								<InputGroupInput
									id='meta-pixel-name'
									value={form.name}
									onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
									placeholder='Основной кабинет'
									disabled={saving}
								/>
							</InputGroup>
						</div>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='meta-pixel-id'>Pixel ID</Label>
							<InputGroup>
								<InputGroupInput
									id='meta-pixel-id'
									value={form.pixelId}
									onChange={e => setForm(prev => ({ ...prev, pixelId: e.target.value }))}
									placeholder='123456789012345'
									inputMode='numeric'
									autoComplete='off'
									disabled={saving}
								/>
							</InputGroup>
							<p className='text-xs text-muted-foreground'>Только цифры, 5–20 символов (ID из Events Manager)</p>
						</div>
						<div className='flex flex-col gap-1.5'>
							<Label>Событие конверсии</Label>
							<Select
								value={form.conversionEvent}
								onValueChange={value =>
									setForm(prev => ({ ...prev, conversionEvent: value as MetaPixelConversionEvent }))
								}
								disabled={saving}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CONVERSION_EVENTS.map(event => (
										<SelectItem key={event} value={event}>
											{META_PIXEL_CONVERSION_EVENT_LABELS[event]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<DialogFooter className='flex-col sm:flex-col'>
							<div className='w-full'>
								<LoadingWrapper show={saving}>
									<Button type='submit' className='w-full' disabled={saving}>
										{editing ? 'Сохранить' : 'Создать'}
									</Button>
								</LoadingWrapper>
							</div>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(deleteTarget)} onOpenChange={open => !open && setDeleteTarget(null)}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>Удалить пиксель?</DialogTitle>
						<DialogDescription>
							Пиксель <strong>{deleteTarget?.name}</strong> будет удалён. Уже созданные ссылки сохранят снапшот и
							продолжат работать.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className='flex-col sm:flex-col'>
						<div className='w-full'>
							<LoadingWrapper show={deleting}>
								<Button
									type='button'
									variant='destructive'
									className='w-full'
									disabled={deleting}
									onClick={() => void handleDelete()}
								>
									Удалить
								</Button>
							</LoadingWrapper>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
